import React, { ReactElement, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';
import styled from 'styled-components';

import { RegisterApiResponse } from 'features/auth';
import { Page, Box, Typography, Flex, HelperText, Loading, Copyright, Button, Checkbox, Label, Icons } from 'components';
import { request, useFormikHandles, phoneMask, URLParams } from 'app/utils';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthInput2 } from 'features/auth/components';
import { useSearchParams } from 'react-router-dom';

const Form = styled.form`
  flex-grow: 1;
`;

const Icon = styled((props) => <Icons {...props} />)`
  color: ${({ theme }) => theme.colors.primary_400};
  width: 65px;
`;

const ExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.gray_500};
  text-decoration: underline;
`;

const RegisterView = (): ReactElement => {
  const { apiGatewayUrl, schoolAlias } = useSchool();
  const { setAlert, clearAlert } = useAlert();
  const [showSuccess, setShowSuccess] = useState(false);

  const initialValues: Record<string, any> = {
    name: '',
    email: '',
    phone: '',
    terms: false,
  };

  const phoneRegExp = /^\([1-9]{2}\) [2-9][0-9]{3,4}-[0-9]{4}/;

  const [searchParams] = useSearchParams();
  const src = searchParams.get('src') || '';

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().required('Você precisa informar seu nome'),
      email: Yup.string().email('O e-mail informado está inválido.').required('Você precisa informar seu e-mail'),
      phone: Yup.string().required('Você precisa informar seu telefone.').matches(phoneRegExp, 'O telefone informado está inválido'),
      terms: Yup.boolean().oneOf([true], 'Você precisa ler e aceitar os termos de uso e privacidade da plataforma'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const { name, email, phone } = values;

      const params = URLParams({
        src,
      });

      request({
        url: `/api/v1/school/registration/${schoolAlias}?${params}`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          name,
          contactEmail: email,
          contactPhone: phone,
        },
      })
        .then((response: AxiosResponse<RegisterApiResponse>) => {
          setSubmitting(false);
          setShowSuccess(true);
        })
        .catch((error: AxiosError<RegisterApiResponse>) => {
          setSubmitting(false);

          if (error.response && error.response.data) {
            const { apiResultMessage } = error.response.data;

            setAlert({
              type: 'error',
              message: apiResultMessage,
            });
            // if (apiResultCode === 2) {
            //   setAlert({
            //     type: 'error',
            //     message: `Ja existe um cadastro com o e-mail <strong>${email}</strong>. <a href="${createUrl(
            //       'forgotPassword'
            //     )}">Clique aqui</a> para redefinir sua senha.`,
            //   });
            // } else {
            // }
          } else {
            setAlert({
              type: 'error',
              message: 'Ocorreu um erro inesperado. Por favor, tente novamente.',
            });
          }
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  useEffect(() => () => clearAlert(), []); // Clear Alert

  return (
    <Page title="Cadastro">
      {formik.isSubmitting && <Loading alpha />}

      <Flex flexGrow={0} flexDirection="column" p={45} bg="dark_350">
        <Typography color="white" fontWeight="bold" upper>
          Criar conta
        </Typography>

        <Typography color="gray_500" mt={2}>
          Preencha os campos abaixo para criar a sua conta.
        </Typography>
      </Flex>

      <Box flexGrow={0}>
        <Alert px={45} py={20} />
      </Box>

      <Flex flexDirection="column">
        {!showSuccess ? (
          <Form onSubmit={formik.handleSubmit}>
            <Flex flexDirection="column">
              <Flex flexGrow={1} flexDirection="column" p={45}>
                {/* name */}
                <Flex flexDirection="column">
                  <label htmlFor="login-name">
                    <Typography type="type2" color="white" upper fontWeight="bold">
                      Nome completo:
                    </Typography>
                  </label>

                  <Box position="relative" mt={2}>
                    <AuthInput2
                      id="login-name"
                      name="name"
                      type="text"
                      onChange={formikHandleChange}
                      onFocus={formikHandleFocus}
                      onBlur={formikHandleBlur}
                      value={formik.values.name}
                      data-haserror={formik.touched.name && formik.errors.name}
                      data-testid="auth-login-name"
                    />
                  </Box>

                  {formik.touched.name && formik.errors.name ? <HelperText type="error">{formik.errors.name}</HelperText> : null}
                </Flex>
                {/* /name */}

                {/* email */}
                <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
                  <label htmlFor="login-email">
                    <Typography type="type2" color="white" upper fontWeight="bold">
                      E-mail:
                    </Typography>
                  </label>

                  <Box position="relative" mt={2}>
                    <AuthInput2
                      id="login-email"
                      name="email"
                      type="text"
                      onChange={formikHandleChange}
                      onFocus={formikHandleFocus}
                      onBlur={formikHandleBlur}
                      value={formik.values.email}
                      data-haserror={formik.touched.email && formik.errors.email}
                      data-testid="auth-login-email"
                    />
                  </Box>

                  {formik.touched.email && formik.errors.email ? <HelperText type="error">{formik.errors.email}</HelperText> : null}
                </Flex>
                {/* /email */}

                {/* phone */}
                <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
                  <label htmlFor="login-phone">
                    <Typography type="type2" color="white" upper fontWeight="bold">
                      Telefone{' '}
                      <Typography as="span" type="type2" color="white" fontWeight="light">
                        (com DDD)
                      </Typography>
                      :
                    </Typography>
                  </label>

                  <Box position="relative" mt={2}>
                    <AuthInput2
                      id="login-phone"
                      name="phone"
                      type="text"
                      onChange={formikHandleChange}
                      onFocus={formikHandleFocus}
                      onBlur={formikHandleBlur}
                      value={phoneMask(formik.values.phone)}
                      data-haserror={formik.touched.phone && formik.errors.phone}
                      data-testid="auth-login-phone"
                      maxLength={15}
                    />
                  </Box>

                  {formik.touched.phone && formik.errors.phone ? <HelperText type="error">{formik.errors.phone}</HelperText> : null}
                </Flex>
                {/* /phone */}

                {/* terms */}
                <Flex flexDirection="column" mt={{ m: 20, d: 30 }}>
                  <Flex position="relative" mt={2} alignItems="flex-start" flexWrap="nowrap">
                    <Checkbox
                      color="gray_500"
                      checked={formik.values.terms}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        formik.setFieldValue('terms', event.currentTarget.checked);
                      }}
                    />

                    <Typography
                      color="gray_500"
                      ml={2}
                      pt="1px"
                      onClick={() => {
                        formik.setFieldValue('terms', !formik.values.terms);
                      }}
                    >
                      <Label htmlFor="register-terms">
                        Declaro que li e aceito os{' '}
                        <ExternalLink href="https://www.melver.com.br/termos-e-politicas/" target="_blank" rel="noreferrer">
                          termos de uso e privacidade
                        </ExternalLink>{' '}
                        da plataforma
                      </Label>
                    </Typography>
                  </Flex>

                  {formik.touched.terms && formik.errors.terms ? <HelperText type="error">{formik.errors.terms}</HelperText> : null}
                </Flex>
                {/* /terms */}

                <Flex mt={8}>
                  <Button width="100%" type="submit" size="medium">
                    Cadastrar
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Form>
        ) : (
          <Flex flexDirection="column" alignItems="center" justifyContent="flex-start" pt={8}>
            <Icon icon="emailFast" />
            <Typography color="primary_400" fontWeight="bold" upper mt={2}>
              Cadastro realizado com sucesso.
            </Typography>
            <Typography color="gray_500" mt={3} style={{ textAlign: 'center' }}>
              Você receberá um e-mail com instruções para criar sua senha e finalizar seu cadastro.
              <br />
              <br />
              Não esqueça deverificar o SPAM.
            </Typography>
          </Flex>
        )}

        <Flex flexGrow={0} justifyContent="center" p={4}>
          <Copyright />
        </Flex>
      </Flex>
    </Page>
  );
};

export default RegisterView;
